import React, { useState, useEffect, useCallback } from 'react';
import gateApi from '../api';

// Plain-language duration. Deliberately no "45m" style abbreviation on its own —
// this screen is read by the loading crew, so it says "45 min" and "2 hr 10 min".
function fmtWait(m) {
  if (m == null) return null;
  // Rounds to whole minutes, so a quick load can come back as 0 — say that in
  // words rather than printing "0 min", which reads as missing data.
  if (m < 1) return 'less than 1 min';
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60), rem = m % 60;
  if (h < 24) return rem ? `${h} hr ${rem} min` : `${h} hr`;
  const d = Math.floor(h / 24);
  return `${d} day${d === 1 ? '' : 's'} ${h % 24} hr`;
}

const RANK_COLORS = [
  { bg: '#FDF2F2', border: '#E9B4B4', accent: '#B91C1C', bar: '#B91C1C' }, // most needed
  { bg: '#FDF6EC', border: '#F0C989', accent: '#8A5106', bar: '#C77B12' },
  { bg: '#F2F7FC', border: '#B5D4F4', accent: '#0C447C', bar: '#185FA5' },
];
const REST = { bg: '#FFFFFF', border: 'var(--border)', accent: '#33352F', bar: '#6B7280' };

/**
 * Loading screen — what the crew should put on the belt next.
 *
 * Deliberately not a dashboard. One idea per card, in the largest type on the
 * site: this material, that many tonnes. Vehicle counts are left out entirely
 * because a truck is not a unit of stone, and showing both numbers invites
 * planning by the wrong one. The waiting time is the loading time itself
 * (weighbridge in, weighbridge out) so a slow pile is visible as a slow pile.
 */
export default function LoadingDemand() {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(false);
  const [at, setAt] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const s = await gateApi.getLiveStats();
      setRows(s.materialDemand || []);
      setAt(new Date());
      setErr(false);
    } catch {
      setErr(true);
    }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 15000);
    return () => clearInterval(id);
  }, [refresh]);

  if (err && rows === null) {
    return (
      <div style={{ padding: '60px 20px', textAlign: 'center' }}>
        <div style={{ fontSize: 17, fontWeight: 600, color: '#B91C1C', marginBottom: 12 }}>
          Could not load
        </div>
        <button onClick={refresh} style={{
          padding: '12px 26px', fontSize: 15, fontWeight: 700, cursor: 'pointer',
          borderRadius: 10, border: '1px solid var(--border)', background: '#fff',
        }}>Try again</button>
      </div>
    );
  }

  if (rows === null) {
    return <div style={{ padding: '60px 20px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 15 }}>Loading…</div>;
  }

  const max = rows.reduce((m, r) => Math.max(m, r.estimatedTonnes), 0) || 1;

  return (
    // No max-width of its own: the dashboard shell is already a fixed 520px
    // column, so anything wider here would be dead. That column is what sets the
    // card count per row — two fit comfortably, three would leave no room for
    // the tonnage figure.
    <div style={{ padding: '20px 16px 36px' }}>

      <div style={{ marginBottom: 4, fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>
        Material needed now
      </div>
      <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16, lineHeight: 1.5 }}>
        Trucks in the yard are waiting for this stone. Load the top one first.
        {at && <> · updated {at.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</>}
      </div>

      {rows.length === 0 ? (
        <div style={{
          background: '#F2FAF4', border: '1px solid #BFE3C9', borderRadius: 14,
          padding: '30px 22px', textAlign: 'center',
        }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#166534', marginBottom: 6 }}>All clear</div>
          <div style={{ fontSize: 14, color: '#166534' }}>No truck is waiting for stone right now.</div>
        </div>
      ) : (
        <>
          {/* Two or three to a row, sized so a card is scannable rather than a
              page of its own. Ordering still carries the instruction, so the
              colour accent is a stripe rather than a filled panel. */}
          <div style={{
            display: 'grid', gap: 12,
            gridTemplateColumns: 'repeat(auto-fill, minmax(232px, 1fr))',
          }}>
            {rows.map((r, i) => {
              const c = RANK_COLORS[i] || REST;
              const wait = fmtWait(r.avgLoadingMinutesToday);
              return (
                <div key={r.material} style={{
                  background: '#fff', border: `1px solid ${c.border}`,
                  borderTop: `3px solid ${c.bar}`, borderRadius: 12, padding: '13px 15px 14px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                    {/* Position, not a score — "1" reads as "do this one first"
                        without needing any of the words around it. */}
                    <span style={{
                      width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                      background: c.bar, color: '#fff', fontSize: 11.5, fontWeight: 700,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontFamily: 'var(--font-mono)',
                    }}>{i + 1}</span>
                    <span style={{ fontSize: 15, fontWeight: 700, color: c.accent }}>
                      {r.material}
                    </span>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                    <span style={{
                      fontSize: 30, fontWeight: 700, color: c.accent,
                      fontFamily: 'var(--font-mono)', lineHeight: 1,
                    }}>
                      {Math.round(r.estimatedTonnes).toLocaleString('en-IN')}
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: c.accent }}>
                      tonnes needed
                    </span>
                  </div>

                  {/* The trucks behind the tonnage. Kept under the figure rather
                      than beside it: tonnes decide which pile to feed, the count
                      tells the crew how long the queue is once they start. */}
                  <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 5 }}>
                    <strong style={{ fontWeight: 700, color: 'var(--text-primary)' }}>
                      {r.waiting}
                    </strong>
                    {r.waiting === 1 ? ' vehicle waiting' : ' vehicles waiting'}
                  </div>

                  <div style={{ height: 5, background: '#F1EFE8', borderRadius: 3, overflow: 'hidden', margin: '10px 0 9px' }}>
                    <div style={{
                      width: `${(r.estimatedTonnes / max) * 100}%`, height: '100%',
                      background: c.bar, borderRadius: 3, minWidth: 3,
                    }} />
                  </div>

                  {/* Still a sentence, not a stat — an empty figure early in the
                      day reads as quiet rather than broken. */}
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.45 }}>
                    {wait
                      ? <>Loading now takes <strong style={{ fontWeight: 700, color: c.accent }}>{wait}</strong> per truck</>
                      : <>Nothing loaded yet today</>}
                  </div>
                  {/* Today's run rate. The queue alone is a snapshot — three
                      trucks of a rare material can outrank the one that sells all
                      day — so what has actually gone out sits beside it. */}
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>
                    Sent out today:{' '}
                    <strong style={{ fontWeight: 700, color: 'var(--text-primary)' }}>
                      {Math.round(r.loadedTodayTonnes).toLocaleString('en-IN')} t
                    </strong>
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 16, lineHeight: 1.6 }}>
            Tonnes are an estimate. A waiting truck has not been loaded yet, so we
            use what trucks of that size normally carry of that material. Time is
            measured from the empty weighing to the loaded weighing.
          </div>
        </>
      )}
    </div>
  );
}
